/**
 * ListAgentMessage 轮询器
 * 定时拉取消息列表并同步到 conversationData，当任务状态非 RUNNING 时执行 onDone 并停止。
 */
import type { Ref } from 'vue';
import type { AgentMessageResponse } from '@/types/formulation/formulation';
import type { MaterialFormulationApi } from '@/domains/material/apis/formulation-api';
import { TASK_STATUS } from '../types';

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 300;

export interface ListMessagePollerDeps {
  sessionId: Ref<string>;
  taskStatus: Ref<string>;
  conversationData: Ref<AgentMessageResponse[] | undefined>;
  materialFormulationApiService: MaterialFormulationApi;
  tryAutoOpenLastToolResult: (list: AgentMessageResponse[] | undefined) => void;
}

export interface ListMessagePoller {
  stop: () => void;
}

/**
 * 创建 ListAgentMessage 轮询器
 * @param sessionIdValue 当前流式会话 ID
 * @param deps 依赖（sessionId、taskStatus、conversationData、API、tryAutoOpenLastToolResult）
 * @param onDone 任务结束或达到最大轮询次数时回调
 */
export function createListAgentMessagePoller(
  sessionIdValue: string,
  deps: ListMessagePollerDeps,
  onDone: () => void,
): ListMessagePoller {
  const {
    sessionId,
    taskStatus,
    conversationData,
    materialFormulationApiService,
    tryAutoOpenLastToolResult,
  } = deps;

  let pollTimerId: ReturnType<typeof setTimeout> | null = null;
  let isPollingActive = true;
  let attempts = 0;

  const poll = async () => {
    if (!isPollingActive || sessionId.value !== sessionIdValue) return;
    if (attempts >= MAX_ATTEMPTS) {
      onDone();
      return;
    }
    attempts += 1;

    try {
      const messages = await materialFormulationApiService.listAgentMessages(sessionIdValue);
      if (sessionId.value === sessionIdValue) {
        conversationData.value = messages;
      }

      const userTask = await materialFormulationApiService.getUserTask(sessionIdValue);
      if (sessionId.value !== sessionIdValue) return;
      taskStatus.value = userTask.status ?? '';

      if (
        userTask.status !== TASK_STATUS.RUNNING &&
        userTask.status !== TASK_STATUS.STARTED
      ) {
        isPollingActive = false;
        if (pollTimerId) {
          clearTimeout(pollTimerId);
          pollTimerId = null;
        }
        try {
          const finalList = await materialFormulationApiService.listAgentMessages(sessionIdValue);
          if (sessionId.value === sessionIdValue && finalList.length > 0) {
            conversationData.value = finalList;
            tryAutoOpenLastToolResult(finalList);
          }
        } catch {
          // ignore
        }
        onDone();
        return;
      }
    } catch {
      // ignore single poll error, continue next tick
    }

    if (isPollingActive) {
      pollTimerId = setTimeout(poll, POLL_INTERVAL_MS);
    }
  };

  pollTimerId = setTimeout(poll, POLL_INTERVAL_MS);

  return {
    stop() {
      isPollingActive = false;
      if (pollTimerId) {
        clearTimeout(pollTimerId);
        pollTimerId = null;
      }
    },
  };
}
